import {Injectable, NgZone, inject} from '@angular/core';
import {Router} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Subscription} from 'rxjs';
import {FirebaseApi} from './firebase-api.service';
import {NotificationData} from '../model/notification-data';
import {NotificationAction} from '../enums/notification-action.enum';
import {UserSubscription} from '../model/user-subscription';
import {SubscriptionPlan} from '../enums/subscription-plan.enum';
import {SubscriptionState} from '../model/subscription-state.enum';
import {Logger} from '../utils/logger';
import {CommonUtil} from '../utils/common.util';
import {ERROR_SNACK_DURATION, openSnack} from '../utils/snack.util';
import {NotificationsApiService} from './api/routes/notifications-api.service';
import {PopupsService} from '../../canvas/services/popups.service';
import {StringsService} from '../../../../projects/common/src/lib/services/strings.service';
import {AuthService} from './auth.service';
import {NotificationSnackbarComponent} from '../components/notification-snackbar.component';
import {formatDateTime, formatDay} from '../utils/intl-format';
import {SupportBadgeService} from '../../dashboard/support/support-badge.service';

/** The colour a notification's badge takes, from the design tokens. */
export type NotificationTone = 'accent' | 'success' | 'warn' | 'error' | 'info' | 'muted';

/** How many notifications the bell keeps. */
const LIST_SIZE = 50;

/**
 * The notifications that go with an email (owner, 2026-10-30: every email has
 * one): their badge, colour, and where a click takes you. Their words are
 * notif_<action>, or notif_<action>_<variant>, filled with `data.args`
 * (formatArg). `clientRoute`: a partner's, about a client's plan.
 */
const EMAIL_NOTICES: {[action: string]: {icon: string, tone: NotificationTone, route?: string, clientRoute?: string}} = {
  [NotificationAction.TWO_FACTOR_ENABLED]: {icon: 'verified_user', tone: 'success', route: '/dashboard/profile?section=security'},
  [NotificationAction.TWO_FACTOR_REMOVED]: {icon: 'gpp_maybe', tone: 'warn', route: '/dashboard/profile?section=security'},
  [NotificationAction.RECOVERY_CODES_ISSUED]: {icon: 'password', tone: 'info', route: '/dashboard/profile?section=security'},
  [NotificationAction.SIGNED_OUT_EVERYWHERE]: {icon: 'logout', tone: 'info', route: '/dashboard/profile?section=security'},
  [NotificationAction.NEW_SIGN_IN]: {icon: 'devices', tone: 'info', route: '/dashboard/profile?section=security'},
  [NotificationAction.EMAIL_CHANGED_BY_SUPPORT]: {icon: 'alternate_email', tone: 'info', route: '/dashboard/profile?section=security'},
  [NotificationAction.ADMIN_RIGHTS_CHANGED]: {icon: 'admin_panel_settings', tone: 'info'},
  [NotificationAction.RELEASE_ANNOUNCEMENT]: {icon: 'new_releases', tone: 'accent', route: '/dashboard/overview'},
  [NotificationAction.SUPPORT_TICKET_RECEIVED]: {icon: 'support_agent', tone: 'info'},
  [NotificationAction.SUPPORT_REPLY]: {icon: 'forum', tone: 'accent'},
  [NotificationAction.PAYMENT_RECEIVED]: {icon: 'receipt_long', tone: 'success', route: '/dashboard/subscription/invoices', clientRoute: '/dashboard/clients'},
  [NotificationAction.REFUND_ISSUED]: {icon: 'currency_exchange', tone: 'info', route: '/dashboard/subscription/invoices'},
  [NotificationAction.SUBSCRIPTION_EXPIRING_SOON]: {icon: 'hourglass_bottom', tone: 'warn', route: '/dashboard/subscription/overview'},
  [NotificationAction.RENEWAL_REMINDER]: {icon: 'event_repeat', tone: 'info', route: '/dashboard/subscription/overview', clientRoute: '/dashboard/clients'},
  [NotificationAction.CARD_EXPIRING]: {icon: 'credit_card', tone: 'warn', route: '/dashboard/subscription/payment-methods'},
  [NotificationAction.SEATS_CHANGED]: {icon: 'group', tone: 'info', route: '/dashboard/subscription/seats'},
  [NotificationAction.SUBSCRIPTION_PAUSED]: {icon: 'pause_circle', tone: 'muted', route: '/dashboard/subscription/overview'},
  [NotificationAction.SUBSCRIPTION_RESUMED]: {icon: 'play_circle', tone: 'success', route: '/dashboard/subscription/overview'},
  [NotificationAction.PAYMENT_SWITCHED]: {icon: 'credit_card', tone: 'success', route: '/dashboard/subscription/payment-methods'},
  [NotificationAction.LEAD_MISSED]: {icon: 'person_off', tone: 'warn', route: '/dashboard/subscription/overview'},
  [NotificationAction.GUEST_PROJECT_STARTED]: {icon: 'person_pin_circle', tone: 'accent', route: '/dashboard/projects'},
  [NotificationAction.REMOVED_FROM_PROJECT]: {icon: 'person_remove', tone: 'muted'},
  [NotificationAction.PROJECTS_OWNERSHIP_TRANSFERRED]: {icon: 'swap_horiz', tone: 'info', route: '/dashboard/projects'},
  [NotificationAction.ENTERPRISE_JOINED]: {icon: 'domain', tone: 'success', route: '/dashboard/overview'},
  [NotificationAction.ENTERPRISE_ADDED]: {icon: 'domain_add', tone: 'accent', route: '/dashboard/overview'},
  [NotificationAction.ENTERPRISE_SSO_CHANGED]: {icon: 'key', tone: 'info', route: '/dashboard/enterprise/sso'},
  [NotificationAction.ENTERPRISE_SEAT_LIMIT]: {icon: 'group_off', tone: 'warn', route: '/dashboard/subscription/seats'},
  [NotificationAction.ENTERPRISE_PASSWORD_SET]: {icon: 'password', tone: 'warn', route: '/dashboard/profile?section=security'},
  [NotificationAction.ENTERPRISE_SIGNED_OUT]: {icon: 'logout', tone: 'warn', route: '/dashboard/profile?section=security'},
  [NotificationAction.ENTERPRISE_MEMBER_SUSPENDED]: {icon: 'block', tone: 'warn'},
  [NotificationAction.PARTNER_INVITATION]: {icon: 'handshake', tone: 'accent', route: '/dashboard/overview'},
  [NotificationAction.PARTNER_ACCEPTED]: {icon: 'how_to_reg', tone: 'success', route: '/dashboard/clients'},
  [NotificationAction.PARTNER_DECLINED]: {icon: 'person_off', tone: 'muted', route: '/dashboard/clients'},
  [NotificationAction.PARTNER_CARD_NEEDED]: {icon: 'credit_card_off', tone: 'warn', route: '/dashboard/subscription/payment-methods'},
  [NotificationAction.PARTNER_STARTED]: {icon: 'handshake', tone: 'success', route: '/dashboard/subscription/overview'},
  [NotificationAction.PARTNER_KEPT]: {icon: 'handshake', tone: 'info', route: '/dashboard/subscription/overview'},
  [NotificationAction.PARTNER_CHANGED]: {icon: 'swap_horiz', tone: 'info', route: '/dashboard/subscription/overview'},
  [NotificationAction.PARTNER_CLIENT_ENDING]: {icon: 'event_busy', tone: 'warn', route: '/dashboard/subscription/overview', clientRoute: '/dashboard/clients'},
  [NotificationAction.PARTNER_CLIENT_ENDED]: {icon: 'event_busy', tone: 'muted', route: '/dashboard/subscription/overview', clientRoute: '/dashboard/clients'},
  [NotificationAction.PARTNER_WITHDRAWN]: {icon: 'cancel', tone: 'muted', route: '/dashboard/subscription/overview'},
  [NotificationAction.PARTNER_INVITATION_LAPSED]: {icon: 'schedule', tone: 'muted', route: '/dashboard/clients'},
  [NotificationAction.API_KEY_CREATED]: {icon: 'vpn_key', tone: 'warn', route: '/dashboard/developer/keys'},
  [NotificationAction.API_KEY_REVOKED]: {icon: 'key_off', tone: 'info', route: '/dashboard/developer/keys'},
  [NotificationAction.API_KEY_SITES_LOOSENED]: {icon: 'public', tone: 'warn', route: '/dashboard/developer/keys'},
  [NotificationAction.API_KEY_STAFF_ACTION]: {icon: 'admin_panel_settings', tone: 'warn', route: '/dashboard/developer/keys'},
  [NotificationAction.WEBHOOK_SECRET_ROTATED]: {icon: 'sync_lock', tone: 'warn', route: '/dashboard/developer/webhooks'},
  [NotificationAction.WEBHOOK_DISABLED]: {icon: 'webhook', tone: 'error', route: '/dashboard/developer/webhooks'},
  [NotificationAction.EMBED_ACCESS_REQUESTED]: {icon: 'lock_open', tone: 'accent', route: '/dashboard/profile?section=developers'},
  [NotificationAction.EMBED_ACCESS_GRANTED]: {icon: 'lock_open', tone: 'success', route: '/dashboard/developer/private-projects'},
  [NotificationAction.EMBED_ACCESS_DECLINED]: {icon: 'block', tone: 'muted', route: '/dashboard/developer/private-projects'},
  [NotificationAction.EMBED_ACCESS_REVOKED]: {icon: 'lock', tone: 'warn', route: '/dashboard/developer/private-projects'},
};

/** Intl wants 'id' where the app says 'in'. */
const INTL_LOCALES: {[lang: string]: string} = {in: 'id'};

/**
 * The notification feed: the bell, its list, the toasts and the organisation
 * link prompt, all from here.
 *
 * The toolbar and the canvas each used to subscribe to this service's events
 * and open their own toasts. Every toolbar instance added a subscription that
 * was never removed, so a notification could appear two or three times; and
 * because the first unseen item was toasted on every snapshot, simply opening
 * the app toasted the same old notification again, as did each unrelated
 * change to any other notification.
 *
 * Now a toast is shown once, for a notification that arrived while the app
 * was open. What was already there is in the bell.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private firebaseApi = inject(FirebaseApi);
  private notificationsApi = inject(NotificationsApiService);
  private popups = inject(PopupsService);
  private stringsService = inject(StringsService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private snackbar = inject(MatSnackBar);
  private ngZone = inject(NgZone);
  private supportBadge = inject(SupportBadgeService);

  notifications: NotificationData[] = [];
  /** The first snapshot has not arrived yet. */
  loading = true;
  /** The feed could not be read; it is retried on its own. */
  failed = false;

  private uid = '';
  private feed: Subscription;
  private retryTimer: any;
  private retries = 0;
  /** Ids already on screen or in the list, so each is toasted at most once. */
  private known = new Set<string>();
  private primed = false;
  /** Link requests already put in front of the user this session. */
  private prompted = new Set<string>();

  constructor() {
    this.authService.userInfoSubject.subscribe(userInfo => {
      const uid = (userInfo && userInfo.uid) || '';
      if (uid === this.uid) {
        return;
      }
      this.stop();
      this.uid = uid;
      if (uid) {
        this.start();
      }
    });
  }

  // ── Feed ────────────────────────────────────────────────────────────

  private start() {
    const ref = this.firebaseApi.getMyNotificationsRef(LIST_SIZE);
    if (!ref) {
      return;
    }
    this.loading = !this.notifications.length;
    this.feed = ref.valueChanges().subscribe({
      next: list => {
        this.retries = 0;
        this.failed = false;
        this.loading = false;
        this.receive(list || []);
      },
      error: err => {
        Logger.error('Notifications feed failed', err);
        this.failed = true;
        this.loading = false;
        this.feed = null;
        // Back off, up to a minute; a signed-out user stops it in stop().
        const delay = Math.min(60000, 2000 * Math.pow(2, this.retries++));
        this.retryTimer = setTimeout(() => this.uid && this.start(), delay);
      },
    });
  }

  private stop() {
    if (this.feed) {
      this.feed.unsubscribe();
      this.feed = null;
    }
    clearTimeout(this.retryTimer);
    this.notifications = [];
    this.known.clear();
    this.prompted.clear();
    this.primed = false;
    this.loading = true;
    this.failed = false;
  }

  /** Retry now, from the list's error state. */
  retry() {
    clearTimeout(this.retryTimer);
    this.retries = 0;
    this.failed = false;
    if (!this.feed && this.uid) {
      this.start();
    }
  }

  private receive(list: NotificationData[]) {
    list.forEach(n => {
      // Written before notifications had a read state: read once seen.
      if (n.read === undefined) {
        n.read = !!n.seen;
      }
    });
    this.notifications = list;

    const arrived = list.filter(n => !this.known.has(n.id));
    arrived.forEach(n => this.known.add(n.id));
    if (!this.primed) {
      // What was there when the app opened is not news.
      this.primed = true;
    } else {
      const fresh = arrived.filter(n => !n.seen && n.action !== NotificationAction.ENTERPRISE_USER_LINK_REQUEST
        && !this.isAboutOpenImport(n));
      if (fresh.length) {
        this.toast(fresh[0]);
      }
      // An answer from support: the menu's number, and the conversation if it is open.
      arrived.filter(n => n.action === NotificationAction.SUPPORT_REPLY)
        .forEach(n => this.supportBadge.replied(String((n.data && n.data.ticket) || '')));
    }

    // An organisation's request to link is a question, not news: it is asked
    // once a session, until it is answered (answering removes it).
    const request = list.find(n => n.action === NotificationAction.ENTERPRISE_USER_LINK_REQUEST && !this.prompted.has(n.id));
    if (request && this.mayInterrupt()) {
      this.prompted.add(request.id);
      this.reviewLinkRequest(request);
    }
  }

  /**
   * Not inside an embedded viewer, and not in the middle of a payment: a
   * dialog over the card form would be the worst place to ask anything.
   */
  private mayInterrupt(): boolean {
    return !CommonUtil.inIframe() && !this.router.url.startsWith('/billing');
  }

  /**
   * An import finished in the project on screen: its chip and its own
   * message say so there. The notification stays in the list.
   */
  private isAboutOpenImport(n: NotificationData): boolean {
    const id = n.action === NotificationAction.IMPORT_FINISHED && n.data && n.data.project
      && (n.data.project.project_id || n.data.project.id);
    return !!id && this.router.url.startsWith(`/app/p/${id}`);
  }

  private toast(n: NotificationData) {
    if (!this.mayInterrupt()) {
      return;
    }
    this.ngZone.run(() => {
      this.snackbar.openFromComponent(NotificationSnackbarComponent, {
        data: n,
        panelClass: 'app-snackbar',
        duration: 8000,
        politeness: 'polite',
      });
    });
  }

  // ── State ───────────────────────────────────────────────────────────

  /** Arrived since the bell was last opened. */
  get unseenCount(): number {
    return this.notifications.filter(n => !n.seen).length;
  }

  get unreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  /** The bell's badge: a number up to 9. */
  get badgeText(): string {
    const count = this.unseenCount;
    return count > 9 ? '9+' : count ? '' + count : '';
  }

  /** The bell was opened: everything in it has been seen, not yet read. */
  markAllSeen() {
    const unseen = this.notifications.filter(n => !n.seen);
    if (!unseen.length) {
      return;
    }
    unseen.forEach(n => n.seen = true);
    this.notificationsApi.setNotificationsSeen().catch(err => {
      Logger.error('Marking notifications seen failed', err);
      unseen.forEach(n => n.seen = false);
    });
  }

  markAllRead() {
    const unread = this.notifications.filter(n => !n.read || !n.seen);
    if (!unread.length) {
      return;
    }
    unread.forEach(n => {
      n.read = true;
      n.seen = true;
    });
    this.notificationsApi.setNotificationsRead().catch(err => {
      Logger.error('Marking notifications read failed', err);
      unread.forEach(n => n.read = false);
      openSnack(this.snackbar, this.getString('error_unknown'), this.getString('dismiss'), ERROR_SNACK_DURATION, 'error');
    });
  }

  markRead(n: NotificationData) {
    if (n.read && n.seen) {
      return;
    }
    n.read = true;
    n.seen = true;
    this.notificationsApi.setNotificationRead(n.id).catch(err => {
      Logger.error('Marking a notification read failed', err);
      n.read = false;
    });
  }

  // ── Presentation ────────────────────────────────────────────────────

  /**
   * The sentence, as HTML (the translations carry <strong>). Everything
   * that came from a person -- names, addresses, project names -- is escaped
   * first: it was dropped into innerHTML as typed.
   */
  getNotificationText(n: NotificationData): string {
    const data = n.data || {};
    const from = data.from || {};
    const who = this.person(from);
    const projectName = this.esc((data.project && data.project.name) || '');
    const org = this.esc(from.company || from.fullName || from.email || '');
    switch (n.action) {
      case NotificationAction.WELCOME_MESSAGE:
        return this.getString('notification_subscription_changed_trial_started', this.esc(this.myName));
      case NotificationAction.ACCOUNT_EMAIL_CHANGED:
        return this.getString('notification_account_email_changed', this.esc(from.email || ''));
      case NotificationAction.ACCOUNT_PASSWORD_CHANGED:
        return this.getString('notification_account_password_changed');
      case NotificationAction.PROJECT_COMMENT_ADDED:
        return this.getString('notif_comment_added', who, projectName);
      case NotificationAction.PROJECT_EDITED_BY:
        return this.getString('notif_project_edited', who, projectName);
      case NotificationAction.PROJECT_MEMBER_INVITE:
        return this.getString('notif_project_shared', who, projectName);
      case NotificationAction.FOLDER_MEMBER_INVITE:
        return this.getString('notif_folder_shared', who, this.esc((data.folder && data.folder.name) || ''));
      case NotificationAction.ENTERPRISE_USER_LINK_REQUEST:
        return this.getString('notif_link_request', org);
      case NotificationAction.ENTERPRISE_USER_UNLINKED:
        return this.getString('notif_unlinked', org);
      case NotificationAction.ENTERPRISE_USER_LINK_APPROVED:
        return this.getString('notif_link_approved', who);
      case NotificationAction.ENTERPRISE_USER_LINK_DECLINED:
        return this.getString('notif_link_declined', who);
      case NotificationAction.ENTERPRISE_ROLE_CHANGED:
        return this.getString('notif_role_changed', org, this.esc(this.roleName(data.role)));
      case NotificationAction.SUBSCRIPTION_CHANGED:
        return this.subscriptionChangedText(data.subscription as UserSubscription, org);
      case NotificationAction.SUBSCRIPTION_STATE_CHANGED:
        return this.subscriptionStateText(data.subscription as UserSubscription);
      // A partner's payment for a client's plan names the client.
      case NotificationAction.PAYMENT_FAILED:
        return data.client
          ? this.getString('notif_payment_failed_client', this.esc(this.amount(data)), this.esc(data.client))
          : this.getString('notif_payment_failed', this.esc(this.amount(data)));
      case NotificationAction.PAYMENT_ACTION_REQUIRED:
        return data.client
          ? this.getString('notif_payment_action_client', this.esc(this.amount(data)), this.esc(data.client))
          : this.getString('notif_payment_action', this.esc(this.amount(data)));
      // A partner's, about a client's guest projects, names the client.
      case NotificationAction.BUDGET_WARNING:
        return data.client ? this.getString('notif_budget_warning_client', this.esc(data.client))
          : this.getString('notif_budget_warning');
      case NotificationAction.BUDGET_LIMIT_REACHED:
        return data.client ? this.getString('notif_budget_limit_client', this.esc(data.client))
          : this.getString('notif_budget_limit');
      case NotificationAction.PLAN_CHANGE_EXPIRED:
        return data.client
          ? this.getString('notif_plan_change_expired_client', this.esc(data.client))
          : this.getString('notif_plan_change_expired');
      case NotificationAction.API_KEY_RETIRING:
        return this.getString('notif_api_key_retiring', this.esc(this.keyLabel(data)),
          this.esc(formatDateTime(Number(data.expires_at) || 0, '' + (this.stringsService.getLanguage() || 'en'))));
      case NotificationAction.GUEST_ESTIMATE_REQUESTED:
        return this.getString('notif_guest_request', this.esc(data.guest_name || this.getString('notif_a_guest')), projectName);
      // A partner's, about a client's plan, names the client.
      case NotificationAction.PRICE_CHANGE:
      case NotificationAction.PRICE_CHANGE_APPLIED: {
        const key = n.action === NotificationAction.PRICE_CHANGE ? 'notif_price_change' : 'notif_price_change_applied';
        const plan = this.esc(this.planName(data.plan_id));
        const date = this.esc(formatDay(Number(data.applies_on) || 0, '' + (this.stringsService.getLanguage() || 'en')));
        return data.client ? this.getString(key + '_client', plan, this.esc(data.client), date)
          : this.getString(key, plan, date);
      }
      case NotificationAction.IMPORT_FINISHED: {
        const items = Number(data.items) || 0;
        const itemsText = items === 1 ? this.getString('import_file_item_one')
          : this.getString('import_file_items', items.toLocaleString());
        return this.getString(NotificationsService.importHadProblems(data) ? 'notif_import_finished_problems'
          : 'notif_import_finished', projectName, this.esc(itemsText));
      }
    }
    return EMAIL_NOTICES[n.action] ? this.emailNoticeText(n) : '';
  }

  /** An email's notification: notif_<action>[_<variant>], with its values in the reader's language. */
  private emailNoticeText(n: NotificationData): string {
    const data: any = n.data || {};
    const key = 'notif_' + n.action + (data.variant ? '_' + data.variant : '');
    const args: string[] = Array.isArray(data.args) ? data.args : [];
    return this.getString(key, ...args.map(a => this.formatArg(a)));
  }

  /**
   * One value from the server ('text:Acme', 'date:<ms>', 'money:<cents>:<currency>',
   * 'plan:<id>', 'count:<n>'), as HTML: what came from a person is escaped.
   */
  private formatArg(arg: string): string {
    const s = String(arg || '');
    const at = s.indexOf(':');
    const kind = at > 0 ? s.slice(0, at) : 'text';
    const value = at > 0 ? s.slice(at + 1) : s;
    const lang = '' + (this.stringsService.getLanguage() || 'en');
    switch (kind) {
      case 'date':
        return this.esc(formatDay(Number(value) || 0, lang));
      case 'money': {
        const [cents, currency] = value.split(':');
        return this.esc(CommonUtil.formatCents(Number(cents) || 0, currency || 'usd'));
      }
      case 'plan':
        return this.esc(this.planName(value));
      case 'count':
        return this.esc((Number(value) || 0).toLocaleString(INTL_LOCALES[lang] || lang));
    }
    return this.esc(value);
  }

  private subscriptionChangedText(sub: UserSubscription, org: string): string {
    if (!sub) {
      return '';
    }
    switch (sub.plan_id) {
      case SubscriptionPlan.TRIAL:
      case SubscriptionPlan.TEST_DAILY:
        return this.getString('notification_subscription_changed_trial_started', this.esc(this.myName));
      case SubscriptionPlan.ENTERPRISE_USER:
        return this.getString('notification_subscription_changed_enterprise_user', org);
    }
    return this.getString('notification_subscription_changed', this.esc(this.planName(sub.plan_id)));
  }

  private subscriptionStateText(sub: UserSubscription): string {
    switch (sub && sub.state) {
      case SubscriptionState.ACTIVE:
        return this.getString('notification_subscription_state_active');
      case SubscriptionState.CANCELLED: {
        // A cancellation at the end of the period: the plan runs on until
        // then, and "now cancelled" said otherwise.
        const scheduled = sub as UserSubscription & {cancel_scheduled?: boolean};
        if (scheduled.cancel_scheduled) {
          const endsOn = Number(scheduled.period_end) || 0;
          return endsOn > 0
            ? this.getString('notification_subscription_state_cancel_scheduled',
              this.esc(formatDay(endsOn, '' + (this.stringsService.getLanguage() || 'en'))))
            : this.getString('notification_subscription_state_cancel_scheduled_soon');
        }
        return this.getString('notification_subscription_state_cancelled');
      }
      case SubscriptionState.PAST_DUE:
      case SubscriptionState.UNPAID:
        return this.getString('notification_subscription_state_unpaid');
      case SubscriptionState.EXPIRED:
        return this.getString('notification_subscription_state_expired');
    }
    return '';
  }

  /** "Name (address)", or whichever of the two there is. */
  private person(from: any): string {
    const name = (from.fullName || '').trim();
    const email = (from.email || '').trim();
    if (name && email) {
      return this.esc(`${name} (${email})`);
    }
    return this.esc(name || email || this.getString('notif_someone'));
  }

  private get myName(): string {
    const info = this.authService.currentUserInfo;
    return (info && (info.fullName || info.email)) || '';
  }

  private planName(planId: string): string {
    let name: string;
    switch (planId) {
      case SubscriptionPlan.PRO_MONTHLY:
      case SubscriptionPlan.PRO_YEARLY:
        name = this.getString('pro');
        break;
      case SubscriptionPlan.ENTERPRISE_MONTHLY:
      case SubscriptionPlan.ENTERPRISE_YEARLY:
        name = this.getString('enterprise');
        break;
      case SubscriptionPlan.OPEN_GUEST_MONTHLY:
        return this.getString('open_guest');
      default:
        name = this.getString('standard');
    }
    const yearly = planId === SubscriptionPlan.PRO_YEARLY || planId === SubscriptionPlan.ENTERPRISE_YEARLY
      || planId === SubscriptionPlan.STANDARD_YEARLY;
    return `${name} (${this.getString(yearly ? 'yearly' : 'monthly')})`;
  }

  private roleName(role: string): string {
    const key = role === 'admin' ? 'role_admin' : role === 'manager' ? 'role_manager'
      : role === 'viewer' ? 'role_viewer' : 'role_member';
    return this.getString(key);
  }

  /** A key as the owner knows it: its name, or the end of it (never the whole key). */
  private keyLabel(data: any): string {
    return (data && data.key_name) || (data && data.key_hint ? '…' + data.key_hint : '');
  }

  private amount(data: any): string {
    return data && data.amount ? CommonUtil.formatCents(data.amount, data.currency) : '';
  }

  private esc(value: string): string {
    return ('' + (value == null ? '' : value))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /** The picture: whoever it is from, or the account's own. */
  getNotificationThumbnail(n: NotificationData): string {
    const fallback = '/assets/images/default_profile_picture.jpg';
    const from = n.data && n.data.from;
    if (from && (from.logoUrl || from.photoUrl) && this.isFromSomeone(n)) {
      return from.logoUrl && this.isFromOrganisation(n) ? from.logoUrl : (from.photoUrl || fallback);
    }
    if (this.isFromSomeone(n)) {
      return fallback;
    }
    const me = this.authService.currentUserInfo;
    return (me && me.photoUrl) || fallback;
  }

  private isFromSomeone(n: NotificationData): boolean {
    switch (n.action) {
      case NotificationAction.PROJECT_COMMENT_ADDED:
      case NotificationAction.PROJECT_EDITED_BY:
      case NotificationAction.PROJECT_MEMBER_INVITE:
      case NotificationAction.FOLDER_MEMBER_INVITE:
      case NotificationAction.ENTERPRISE_USER_LINK_REQUEST:
      case NotificationAction.ENTERPRISE_USER_LINK_APPROVED:
      case NotificationAction.ENTERPRISE_USER_LINK_DECLINED:
      case NotificationAction.ENTERPRISE_USER_UNLINKED:
      case NotificationAction.ENTERPRISE_ROLE_CHANGED:
        return true;
    }
    return false;
  }

  private isFromOrganisation(n: NotificationData): boolean {
    return n.action === NotificationAction.ENTERPRISE_USER_LINK_REQUEST
      || n.action === NotificationAction.ENTERPRISE_USER_UNLINKED
      || n.action === NotificationAction.ENTERPRISE_ROLE_CHANGED;
  }

  getNotificationSecondaryIcon(n: NotificationData): string {
    switch (n.action) {
      case NotificationAction.WELCOME_MESSAGE: return 'waving_hand';
      case NotificationAction.ACCOUNT_PASSWORD_CHANGED: return 'lock';
      case NotificationAction.ACCOUNT_EMAIL_CHANGED: return 'alternate_email';
      case NotificationAction.PROJECT_MEMBER_INVITE: return 'share';
      case NotificationAction.PROJECT_EDITED_BY: return 'edit';
      case NotificationAction.PROJECT_COMMENT_ADDED: return 'chat_bubble';
      case NotificationAction.FOLDER_MEMBER_INVITE: return 'folder_shared';
      case NotificationAction.ENTERPRISE_USER_LINK_APPROVED: return 'how_to_reg';
      case NotificationAction.ENTERPRISE_USER_LINK_DECLINED: return 'person_off';
      case NotificationAction.ENTERPRISE_USER_LINK_REQUEST: return 'domain_add';
      case NotificationAction.ENTERPRISE_USER_UNLINKED: return 'link_off';
      case NotificationAction.ENTERPRISE_ROLE_CHANGED: return 'badge';
      case NotificationAction.SUBSCRIPTION_CHANGED: return 'workspace_premium';
      case NotificationAction.SUBSCRIPTION_STATE_CHANGED: return 'credit_card';
      case NotificationAction.PAYMENT_FAILED: return 'credit_card_off';
      case NotificationAction.PAYMENT_ACTION_REQUIRED: return 'verified_user';
      case NotificationAction.BUDGET_WARNING: return 'trending_up';
      case NotificationAction.BUDGET_LIMIT_REACHED: return 'block';
      case NotificationAction.GUEST_ESTIMATE_REQUESTED: return 'request_quote';
      case NotificationAction.PLAN_CHANGE_EXPIRED: return 'event_busy';
      case NotificationAction.API_KEY_RETIRING: return 'key_off';
      case NotificationAction.PRICE_CHANGE: return 'sell';
      case NotificationAction.PRICE_CHANGE_APPLIED: return 'event_available';
      case NotificationAction.IMPORT_FINISHED: return 'upload_file';
    }
    return (EMAIL_NOTICES[n.action] && EMAIL_NOTICES[n.action].icon) || 'notifications';
  }

  getNotificationTone(n: NotificationData): NotificationTone {
    switch (n.action) {
      case NotificationAction.PAYMENT_FAILED:
      case NotificationAction.BUDGET_LIMIT_REACHED:
        return 'error';
      case NotificationAction.PAYMENT_ACTION_REQUIRED:
      case NotificationAction.BUDGET_WARNING:
      case NotificationAction.PLAN_CHANGE_EXPIRED:
      case NotificationAction.API_KEY_RETIRING:
      case NotificationAction.ENTERPRISE_USER_LINK_REQUEST:
      case NotificationAction.ENTERPRISE_USER_UNLINKED:
      case NotificationAction.PRICE_CHANGE:
        return 'warn';
      case NotificationAction.PRICE_CHANGE_APPLIED:
        return 'info';
      case NotificationAction.SUBSCRIPTION_STATE_CHANGED: {
        const state = n.data && n.data.subscription && n.data.subscription.state;
        return state === SubscriptionState.ACTIVE ? 'success'
          : state === SubscriptionState.CANCELLED ? 'muted' : 'error';
      }
      case NotificationAction.ENTERPRISE_USER_LINK_APPROVED:
      case NotificationAction.SUBSCRIPTION_CHANGED:
      case NotificationAction.GUEST_ESTIMATE_REQUESTED:
        return 'success';
      case NotificationAction.PROJECT_COMMENT_ADDED:
      case NotificationAction.PROJECT_MEMBER_INVITE:
      case NotificationAction.FOLDER_MEMBER_INVITE:
      case NotificationAction.PROJECT_EDITED_BY:
        return 'accent';
      case NotificationAction.ENTERPRISE_ROLE_CHANGED:
        return 'info';
      case NotificationAction.IMPORT_FINISHED:
        return NotificationsService.importHadProblems(n.data) ? 'warn' : 'success';
    }
    return (EMAIL_NOTICES[n.action] && EMAIL_NOTICES[n.action].tone) || 'muted';
  }

  /** Files that could not be imported, or a Replace that could not clear the old map. */
  private static importHadProblems(data: any): boolean {
    return !!data && ((Number(data.failed) || 0) > 0 || !!data.not_cleared);
  }

  /** "3 minutes ago", in the app's language. */
  getNotificationTimestamp(n: NotificationData): string {
    const ms = NotificationsService.millis(n.timestamp);
    if (!ms) {
      return '';
    }
    const lang = '' + (this.stringsService.getLanguage() || 'en');
    const seconds = Math.round((ms - Date.now()) / 1000);
    const abs = Math.abs(seconds);
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    try {
      const rtf = new Intl.RelativeTimeFormat(INTL_LOCALES[lang] || lang, {numeric: 'auto'});
      for (const [unit, size] of units) {
        if (abs >= size) {
          return rtf.format(Math.round(seconds / size), unit);
        }
      }
      return rtf.format(0, 'second');
    } catch (e) {
      return new Date(ms).toLocaleString();
    }
  }

  /** The full date, for the tooltip. */
  getNotificationDate(n: NotificationData): string {
    const ms = NotificationsService.millis(n.timestamp);
    const lang = '' + (this.stringsService.getLanguage() || 'en');
    try {
      return ms ? new Date(ms).toLocaleString(INTL_LOCALES[lang] || lang, {dateStyle: 'medium', timeStyle: 'short'}) : '';
    } catch (e) {
      return ms ? new Date(ms).toLocaleString() : '';
    }
  }

  /** Firestore hands back a Timestamp; older code wrote numbers. */
  static millis(t: any): number {
    if (!t) {
      return 0;
    }
    if (typeof t === 'number') {
      return t;
    }
    if (typeof t.toMillis === 'function') {
      return t.toMillis();
    }
    return t.seconds ? t.seconds * 1000 : 0;
  }

  // ── Actions ─────────────────────────────────────────────────────────

  hasNotificationAction(n: NotificationData): boolean {
    return !!this.getNotificationActionText(n);
  }

  getNotificationActionText(n: NotificationData): string {
    switch (n.action) {
      case NotificationAction.ENTERPRISE_USER_LINK_REQUEST:
        return this.getString('review_request');
      case NotificationAction.PAYMENT_FAILED:
        return this.getString(n.data && n.data.client ? 'partner_open_clients' : 'notif_update_card');
      case NotificationAction.PAYMENT_ACTION_REQUIRED:
        return this.getString('notif_confirm_payment');
    }
    return '';
  }

  onNotificationActionClick($event: Event, n: NotificationData) {
    $event.stopPropagation();
    this.markRead(n);
    switch (n.action) {
      case NotificationAction.ENTERPRISE_USER_LINK_REQUEST:
        this.reviewLinkRequest(n);
        break;
      case NotificationAction.PAYMENT_FAILED:
        this.go(n.data && n.data.client ? '/dashboard/clients' : '/dashboard/subscription/payment-methods');
        break;
      case NotificationAction.PAYMENT_ACTION_REQUIRED: {
        // Stripe's own page asks for the bank's check; the overview's retry
        // does the same when the link is gone.
        const url = n.data && n.data.hosted_invoice_url;
        if (url && /^https:\/\/[a-z0-9.-]*stripe\.com\//i.test(url)) {
          window.open(url, '_blank', 'noopener');
        } else {
          this.go(n.data && n.data.client ? '/dashboard/clients' : '/dashboard/subscription/overview');
        }
        break;
      }
    }
  }

  /** Where a notification leads. */
  onNotificationClick(n: NotificationData) {
    this.markRead(n);
    const data = n.data || {};
    switch (n.action) {
      case NotificationAction.WELCOME_MESSAGE:
        this.go('/dashboard');
        break;
      case NotificationAction.ACCOUNT_EMAIL_CHANGED:
      case NotificationAction.ACCOUNT_PASSWORD_CHANGED:
        // Where the devices, password and sign-in email are, not the Profile tab.
        this.go('/dashboard/profile?section=security');
        break;
      case NotificationAction.PROJECT_EDITED_BY:
      case NotificationAction.PROJECT_COMMENT_ADDED:
      case NotificationAction.PROJECT_MEMBER_INVITE:
      case NotificationAction.GUEST_ESTIMATE_REQUESTED:
      case NotificationAction.IMPORT_FINISHED: {
        const id = data.project && (data.project.project_id || data.project.id);
        this.go(id ? `/app/p/${id}` : '/dashboard/projects');
        break;
      }
      case NotificationAction.FOLDER_MEMBER_INVITE:
        this.go('/dashboard/projects');
        break;
      case NotificationAction.ENTERPRISE_USER_LINK_REQUEST:
        this.reviewLinkRequest(n);
        break;
      case NotificationAction.ENTERPRISE_USER_LINK_APPROVED:
      case NotificationAction.ENTERPRISE_USER_LINK_DECLINED:
        this.go('/dashboard/enterprise');
        break;
      case NotificationAction.ENTERPRISE_ROLE_CHANGED:
        this.go('/dashboard/overview');
        break;
      case NotificationAction.PAYMENT_FAILED:
        this.go(n.data && n.data.client ? '/dashboard/clients' : '/dashboard/subscription/payment-methods');
        break;
      case NotificationAction.BUDGET_WARNING:
      case NotificationAction.BUDGET_LIMIT_REACHED:
        // A partner's, about a client's guest projects: its Clients page.
        this.go(data.client ? '/dashboard/clients' : '/dashboard/subscription/usage');
        break;
      case NotificationAction.API_KEY_RETIRING:
        // The key's card, where the warning and "Generate new key" are.
        this.go('/dashboard/developer/keys');
        break;
      case NotificationAction.PAYMENT_ACTION_REQUIRED:
      case NotificationAction.PLAN_CHANGE_EXPIRED:
      // A price change: the Subscription page, whose notice has both prices.
      case NotificationAction.PRICE_CHANGE:
      case NotificationAction.PRICE_CHANGE_APPLIED:
        // A partner's, about a client's plan: its Clients page.
        this.go(data.client ? '/dashboard/clients' : '/dashboard/subscription/overview');
        break;
      // Support: the request's conversation, where the answer is (and can be answered).
      case NotificationAction.SUPPORT_REPLY:
      case NotificationAction.SUPPORT_TICKET_RECEIVED: {
        const ticket = typeof data.ticket === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(data.ticket) ? data.ticket : '';
        this.go(ticket ? `/dashboard/support/${ticket}` : '/dashboard/support');
        break;
      }
      case NotificationAction.SUBSCRIPTION_CHANGED:
      case NotificationAction.SUBSCRIPTION_STATE_CHANGED:
      case NotificationAction.ENTERPRISE_USER_UNLINKED:
        this.go('/dashboard/subscription/overview');
        break;
      default: {
        // An email's notification: where its table says (a partner's, about
        // a client's plan, to its Clients page). A partner plan's ending goes
        // to both, and there `variant` is the reader: the client's copy says
        // 'client' and belongs on their own Subscription page.
        const notice = EMAIL_NOTICES[n.action];
        const ending = n.action === NotificationAction.PARTNER_CLIENT_ENDING
          || n.action === NotificationAction.PARTNER_CLIENT_ENDED;
        const forClient = ending
          ? data.variant === 'partner'
          : !!data.client || data.variant === 'client' || data.variant === 'partner';
        const route = notice && ((forClient && notice.clientRoute) || notice.route);
        if (route) {
          this.go(route);
        }
      }
    }
  }

  private go(url: string) {
    this.ngZone.run(() => this.router.navigateByUrl(url));
  }

  /**
   * Ask whether to join the organisation, and say what came of it.
   *
   * Accepting changes the account's plan and role on the server; the role
   * reaches the API through the sign-in token, so that is refreshed rather
   * than the whole page reloaded -- the reload threw away the message saying
   * it had worked.
   */
  reviewLinkRequest(n: NotificationData) {
    const org = (n.data && n.data.from && (n.data.from.company || n.data.from.email)) || '';
    this.ngZone.run(() => {
      this.popups.showAccountLinkRequestPopup(n).then(async answer => {
        if (answer === true) {
          try {
            await this.authService.getIdToken();
          } catch (e) {
            Logger.error('Token refresh after linking failed', e);
          }
          await this.router.navigateByUrl('/dashboard/overview');
          openSnack(this.snackbar, this.getString('account_link_accepted', org), this.getString('dismiss'), 6000, 'success');
        } else if (answer === false) {
          openSnack(this.snackbar, this.getString('account_link_rejected', org), this.getString('dismiss'), 4000, 'neutral');
        }
        // Closed without an answer: the request stays in the bell.
      }).catch(err => {
        const code = err && err.msg && err.msg.errorCode;
        const key = typeof code === 'string' && this.getString(code) ? code : 'error_account_link_accept';
        openSnack(this.snackbar, this.getString(key), this.getString('dismiss'), ERROR_SNACK_DURATION, 'error');
      });
    });
  }

  getString(key: string, ...args: string[]): string {
    const lang = this.stringsService.getLanguage();
    return lang ? this.stringsService.getString(key, lang, ...args) : '';
  }
}
